"use client";

import React from 'react';

interface PointsBadgeProps {
  points: number;
  className?: string;
}

/**
 * Format points value for display
 * - Integers show without decimals: 1, 2, 5, 10, 100
 * - Decimals show with minimal precision: 0.5
 * Note: Value may come as string from API (PostgreSQL DECIMAL)
 */
export function formatPoints(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isInteger(num)) {
    return num.toString();
  }
  // Remove trailing zeros (e.g., 0.50 -> 0.5)
  return parseFloat(num.toFixed(2)).toString();
}

/**
 * PointsBadge - Displays points value as an overlay badge on NFT images
 *
 * Positioned bottom-left to avoid conflicts with:
 * - Featured badge (top-left)
 * - Discount badge (top-right)
 * - Selection indicator (top-right)
 * - Bundle count badge (bottom-right)
 *
 * Only shows when points > 0 (null and 0 are hidden)
 */
export function PointsBadge({ points, className = '' }: PointsBadgeProps) {
  // Don't render for null, undefined, or 0 points
  // Convert to number first since it may come as string from API
  const numPoints = typeof points === 'number' ? points : parseFloat(String(points));
  if (!numPoints || numPoints <= 0 || isNaN(numPoints)) {
    return null;
  }

  const formattedPoints = formatPoints(numPoints);

  return (
    <div
      className={`absolute bottom-2 left-2 rounded-full bg-black/60 px-2 ${className}`}
    >
      <span className="text-xs font-semibold text-white">
        {formattedPoints} {numPoints === 1 ? 'pt' : 'pts'}
      </span>
    </div>
  );
}
